<script setup lang="ts">
import { toRefs, ref } from 'vue';
import PreviewFileModal from '@/components/MessageCenter/components/Chat/components/PreviewFileModal.vue';
import PreviewMessageFile from '@/components/MessageCenter/components/Chat/components/PreviewMessageFile.vue';
import MessageActions from '@/components/MessageCenter/components/Chat/components/MessageActions.vue';
import { EMIT, PERMISSIONS } from '@/enum';
import { can, useDateTime } from '@/composables';

interface PropsInterface {
  createdAt: Date | string;
  content: string | null;
  attachments?: string[];
  displayName: string;
  isYou: boolean;
  messageId?: number;
}

const props = defineProps<PropsInterface>();
const { createdAt, content, displayName, isYou, messageId, attachments } = toRefs(props);
const showPreviewModal = ref(false);

const openFilePreview = () => {
  if (can([PERMISSIONS.MESSAGE_DOWNLOAD_MESSAGE_FILE])) showPreviewModal.value = true;
};

const closeFilePreview = () => {
  showPreviewModal.value = false;
};
</script>

<template>
  <div class="flex flex-column my-2" :class="isYou ? 'align-items-end' : 'align-items-start'">
    <div class="flex align-items-center w-fit my-1" :class="!isYou ? 'flex-row-reverse' : ''">
      <p class="text-xxs">{{ useDateTime().formatDateTimeForMessageFromUTC(createdAt) }}</p>
      <p class="font-bold" :class="isYou ? 'ml-2' : 'mr-2'">{{ isYou ? 'You' : displayName }}</p>
    </div>

    <div class="flex align-items-start w-fit">
      <div v-if="isYou && can([PERMISSIONS.MESSAGE_DESTROY])" class="mr-2 shrink-0">
        <MessageActions :message-id="messageId" :created-at="createdAt" :action="EMIT.DELETE_MESSAGE" />
      </div>
      <div
        class="bg-white p-2 text-sm border-round-xs border-round-bottom-md word-break max-w-17rem"
        :class="isYou ? 'border-round-left-md' : 'border-round-right-md'"
      >
        {{ content }}

        <PreviewMessageFile
          v-for="(attachment, index) in attachments"
          :key="index"
          :file="attachment"
          :preview-file="true"
          :document-id="attachment.id"
          :message-id="messageId"
          :created-at="createdAt"
          :is-you="isYou"
          :metadata="attachment.metadata"
          :tag="'div'"
          class="preview-message-file-list"
          @show-preview="openFilePreview"
        />
      </div>
    </div>

    <PreviewFileModal :visible="showPreviewModal" title="Preview File" @cancel="closeFilePreview" />
  </div>
</template>

<style lang="scss">
.text-xxs {
  font-size: 10px;
}
.word-break {
  overflow-wrap: break-word;
}

.preview-audio {
  width: 100%;
}

.preview-message-file-list {
  margin: 0;
  padding: 0;
  width: 15rem !important;
}
</style>
